import React, { useRef, useEffect, useState } from "react";
import { usePopper } from "react-popper";
import cn from "classnames";
import style from "./NotificationsPopup.module.scss";
import CloseIconNew from "../../shared/icons/CloseIconNew";
import { Notification } from "../../../services";
import { t } from "i18next";
import OrdersChatHub from "../../shared/chat/OrdersChatHub";

const NotificationsPopup = ({
  notifications,
  orderData,
  orderType = "order",
  isOpen,
  onClose,
  referenceElement,
  onNotificationsRead,
  onNotificationClick,
}) => {
  const popupRef = useRef(null);
  const containerRef = useRef(null);
  const chatHubRef = useRef(null);
  const [popperElement, setPopperElement] = useState(null);
  const [isChatHubOpen, setIsChatHubOpen] = useState(false);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: "left-start",
    modifiers: [
      {
        name: "preventOverflow",
        options: { boundary: "viewport" },
      },
      {
        name: "offset",
        options: { offset: [0, 2] },
      },
    ],
  });

  useEffect(() => {
    const markAllAsRead = async () => {
      if (!isOpen) {
        const toMark = notifications.filter((n) => !n.isRead);

        try {
          await Promise.all(toMark.map((n) => Notification.markOrderNotificationAsRead(n.id)));

          if (onNotificationsRead && toMark.length > 0) {
            onNotificationsRead(toMark.map((n) => n.id));
          }
        } catch (err) {
          console.error("Ошибка при отметке уведомлений как прочитанных", err);
        }
      }
    };

    markAllAsRead();
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsidePopup = popupRef.current && !popupRef.current.contains(event.target);
      const isOutsideChat = chatHubRef.current && !chatHubRef.current.contains(event.target);

      if (isOutsidePopup && (!isChatHubOpen || isOutsideChat)) {
        setIsChatHubOpen(false);
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, isChatHubOpen, onClose]);

  const handleNotificationClick = (notification) => {
    if (notification.text === "Новое сообщение в чате") {
      setIsChatHubOpen(true);
    } else if (onNotificationClick) {
      onNotificationClick(notification);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div ref={setPopperElement} style={styles.popper} {...attributes.popper} className={cn(style.popup)}>
        <div ref={popupRef} className={style.inner}>
          <div className={style.header}>
            {t("notificationPage.header.title")}
            <span className={style.close} onClick={onClose}>
              <CloseIconNew />
            </span>
          </div>
          <div ref={containerRef} className={style.list}>
            {notifications.length === 0 ? (
              <div className={style.empty}>{t("notificationPage.noNotifications")}</div>
            ) : (
              [...notifications]
                .sort((a, b) => Number(a.isRead) - Number(b.isRead))
                .map((n) => (
                  <div
                    key={n.id}
                    className={cn(style.notification, { [style.unread]: !n.isRead })}
                    onClick={() => handleNotificationClick(n)}
                    style={{ cursor: "pointer" }}
                  >
                    {!n.isRead && <span className={style.unreadDot} />}
                    <div className={style.message}>{n.text}</div>
                    <div className={style.timestamp}>
                      {n.readDate
                        ? t("notificationPage.opened", { date: new Date(n.readDate).toLocaleString() })
                        : t("notificationPage.unread")}
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>
      </div>

      <OrdersChatHub
        ref={chatHubRef}
        isOpen={isChatHubOpen}
        orderId={orderData?.id}
        orderData={orderData}
        onClose={() => setIsChatHubOpen(false)}
        serviceType={orderType}
        orderNumber={orderData.number}
      />
    </>
  );
};

export default NotificationsPopup;
